import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { PageContent } from '../components/layout/PageContent';
import { CabecalhoPagina } from '../components/layout/CabecalhoPagina';
import { Dica } from '../components/common/Formulario';
import { fightTimeApi, championshipApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Timer, Save, RotateCcw, ShieldCheck, Globe, Trophy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Wave 5 — Fight time rules editor.
 * Global table only (per-championship overrides will live inside the
 * championship's "Estrutura do Evento" tab in a future iteration).
 * Visible only to super_admin / federation.
 */

const AGE_CLASSES = [
  'Pre-Mirim',
  'Mirim',
  'Infantil A',
  'Infantil B',
  'Infanto-Juvenil A',
  'Infanto-Juvenil B',
  'Juvenil',
  'Adulto',
  'Master 1', 'Master 2', 'Master 3', 'Master 4', 'Master 5', 'Master 6',
];

const BELTS = ['Branca', 'Cinza', 'Amarela', 'Laranja', 'Verde', 'Azul', 'Roxa', 'Marrom', 'Preta'];

export default function FightTimesPage() {
  const { user } = useAuth();
  const isAllowed = ['super_admin', 'federation'].includes(user?.role);

  const [rules, setRules] = useState([]);
  const [globalRules, setGlobalRules] = useState([]);  // always loaded as fallback reference
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Wave 5 — scope selector. null = global table; any string = championship override.
  const params = useParams();
  const contextId = params.id || null;
  const [scope, setScope] = useState(contextId);
  const [championships, setChampionships] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [scopedRes, globalRes, champsRes] = await Promise.all([
        fightTimeApi.list(scope ? { championship_id: scope } : {}),
        scope ? fightTimeApi.list({}) : Promise.resolve({ data: null }),
        championshipApi.list().catch(() => ({ data: [] })),
      ]);
      setRules(scopedRes.data || []);
      if (globalRes.data) setGlobalRules(globalRes.data);
      else setGlobalRules(scopedRes.data || []);
      setChampionships(champsRes.data || []);
    } catch (e) {
      toast.error('Erro ao carregar regras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAllowed) load(); else setLoading(false); }, [isAllowed, scope]);

  // Build a quick lookup map: "age_class|belt" -> minutes (current scope)
  const matrix = useMemo(() => {
    const m = {};
    rules.forEach((r) => { m[`${r.age_class}|${r.belt}`] = r.minutes; });
    return m;
  }, [rules]);

  // Global lookup (used as "fallback hint" when editing a championship override)
  const globalMatrix = useMemo(() => {
    const m = {};
    globalRules.forEach((r) => { m[`${r.age_class}|${r.belt}`] = r.minutes; });
    return m;
  }, [globalRules]);

  // Local edit buffer (key -> string value)
  const [edits, setEdits] = useState({});
  const cellValue = (age, belt) => {
    const k = `${age}|${belt}`;
    if (edits[k] !== undefined) return edits[k];
    return matrix[k] ?? '';
  };
  const setCell = (age, belt, v) => {
    const k = `${age}|${belt}`;
    setEdits((prev) => ({ ...prev, [k]: v.replace(/[^0-9]/g, '') }));
  };

  const hasChanges = Object.keys(edits).length > 0;
  const isOverride = !!scope;
  const selectedChamp = championships.find((c) => c._id === scope);

  const handleSave = async () => {
    setSaving(true);
    try {
      const allRules = [];
      AGE_CLASSES.forEach((age) => {
        BELTS.forEach((belt) => {
          const v = cellValue(age, belt);
          if (v !== '' && v !== null && !isNaN(parseInt(v, 10))) {
            allRules.push({ age_class: age, belt, minutes: parseInt(v, 10) });
          }
        });
      });
      await fightTimeApi.upsert({ rules: allRules, championship_id: scope });
      toast.success(
        isOverride
          ? `${allRules.length} overrides salvos para "${selectedChamp?.name || 'campeonato'}"`
          : `${allRules.length} regras globais salvas`
      );
      setEdits({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async () => {
    const msg = isOverride
      ? `Remover todos os overrides deste campeonato? Ele voltará a usar a tabela global.`
      : 'Restaurar a tabela GLOBAL para o padrão CBJJ? Isso substituirá os valores globais atuais.';
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      await fightTimeApi.restoreDefaults(scope ? { championship_id: scope } : {});
      toast.success(isOverride ? 'Overrides removidos' : 'Padrão CBJJ restaurado');
      setEdits({});
      load();
    } catch (e) {
      toast.error('Erro ao restaurar');
    } finally {
      setSaving(false);
    }
  };

  if (!isAllowed) {
    return (
      <PageContent>
        <Card className="bg-[#141414] border-[#27272A] max-w-md mx-auto mt-12">
          <CardContent className="p-8 text-center">
            <ShieldCheck className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-2">Acesso restrito</h2>
            <p className="text-[#A1A1AA] text-sm">A configuração de Tempos de Luta é visível apenas para Super Admin e Federações.</p>
          </CardContent>
        </Card>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <div className="space-y-6" data-testid="fight-times-page">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <CabecalhoPagina
            className="min-w-0 flex-1"
            contexto="Configuração"
            titulo="Tempos de Luta"
            descricao="Tabela de tempo de luta (em minutos) por classe etária × faixa."
            icone={Timer}
          />
          <div className="flex gap-2">
            <Button onClick={handleRestore} disabled={saving} variant="outline" data-testid="restore-defaults-btn" className="border-[#27272A] text-[#A1A1AA] hover:text-white">
              <RotateCcw size={16} className="mr-2" /> {isOverride ? 'Limpar Overrides' : 'Restaurar Padrão CBJJ'}
            </Button>
            <Button onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-fight-times-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              Salvar
            </Button>
          </div>
        </div>

        {/* SCOPE SELECTOR — global vs per-championship override */}
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-sm font-bold text-[#A1A1AA] uppercase tracking-wider">Escopo:</label>
              <Select value={scope || 'global'} onValueChange={(v) => { setScope(v === 'global' ? null : v); setEdits({}); }}>
                <SelectTrigger data-testid="scope-select" className="bg-[#1A1A1A] border-[#27272A] text-white max-w-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  <SelectItem value="global" className="text-white">
                    <span className="flex items-center gap-2"><Globe size={16} /> Tabela GLOBAL (vale para todos)</span>
                  </SelectItem>
                  {championships.map((c) => (
                    <SelectItem key={c._id} value={c._id} className="text-white">
                      <span className="flex items-center gap-2"><Trophy size={16} className="text-[#D4AF37]" /> Override: {c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Visual banner indicating current scope */}
            {!isOverride ? (
              <div className="mt-3 flex items-start gap-2 p-3 rounded bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm">
                <Globe size={16} className="shrink-0 mt-0.5" />
                <div>
                  <strong>Você está editando a TABELA GLOBAL.</strong>
                  <p className="text-xs mt-1 opacity-90">Esses valores valem para TODOS os campeonatos do sistema (exceto os que tiverem override específico abaixo).</p>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex items-start gap-2 p-3 rounded bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#D4AF37] text-sm">
                <Trophy size={16} className="shrink-0 mt-0.5" />
                <div>
                  <strong>Você está editando um OVERRIDE para: {selectedChamp?.name || '—'}</strong>
                  <p className="text-xs mt-1 opacity-90">As alterações aqui valem APENAS para este campeonato. Células em branco voltam a usar a tabela global. Use o botão &ldquo;Limpar Overrides&rdquo; para apagar todos e usar 100% da global.</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {hasChanges && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded p-3 text-sm text-[#D4AF37]">
            Você tem {Object.keys(edits).length} alteração(ões) não salva(s). Clique em &ldquo;Salvar&rdquo; para persistir.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
        ) : (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="tabela tabela--densa w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#27272A] text-[#71717A] text-xs uppercase tracking-wider">
                      <th className="py-3 px-3 text-left sticky left-0 bg-[#141414] z-10 min-w-[160px]">Classe Etária</th>
                      {BELTS.map((b) => (
                        <th key={b} className="py-3 px-2 text-center min-w-[80px]">{b}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {AGE_CLASSES.map((age, idx) => (
                      <tr key={age} className={`border-b border-[#27272A]/40 ${idx % 2 === 0 ? 'bg-[#0F0F0F]' : ''}`}>
                        <td className="py-2 px-3 text-white font-medium sticky left-0 bg-inherit z-10">{age}</td>
                        {BELTS.map((belt) => {
                          const k = `${age}|${belt}`;
                          const edited = edits[k] !== undefined;
                          const globalFallback = isOverride ? globalMatrix[k] : undefined;
                          const placeholder = globalFallback !== undefined ? String(globalFallback) : '—';
                          return (
                            <td key={belt} className="py-2 px-1 text-center">
                              <Input
                                type="text"
                                inputMode="numeric"
                                value={cellValue(age, belt)}
                                onChange={(e) => setCell(age, belt, e.target.value)}
                                placeholder={placeholder}
                                data-testid={`cell-${age.replace(/\s+/g, '_')}-${belt}`}
                                title={isOverride && globalFallback !== undefined ? `Global: ${globalFallback} min` : ''}
                                className={`text-center h-9 bg-[#1A1A1A] border-[#27272A] text-white placeholder:text-[#52525B] px-1 ${edited ? 'ring-1 ring-[#D4AF37]/60' : ''}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Dica icone={false} className="p-3 border-t border-[#27272A]">
                Valores em minutos. {isOverride
                  ? 'No modo OVERRIDE, células em branco usam o valor da tabela global (mostrado em cinza como placeholder). Preencha apenas o que quer customizar para este evento.'
                  : 'Tabela global usada por todos os campeonatos. Células em branco aparecerão vazias no PDF do Staff.'} Células editadas ficam destacadas em dourado até salvar.
              </Dica>
            </CardContent>
          </Card>
        )}
      </div>
    </PageContent>
  );
}
